'use client'
import { t } from 'i18next';
import { ContentHeader } from "../layout/ContentHeader";
import { useEffect, useState } from 'react';
import { Card, CardContent, Typography, TextField, Box, Grid, Button } from '@mui/material';
import { Download } from '@mui/icons-material';
import TitleBar from '../layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useLastLocationVisits } from '@/hooks/api/useLocationVisits';
import { useLocations } from '@/hooks/useLocation';
import { ILocation } from '@/stack-shared/interfaces/model/location.interface';
import SiteVisitOverviewCard from '../site-visit/LocationVisitCardOverview';
import ReportRejectActionButton from '../reject/ReportRejectActionButton';

function LocationServiceSearchPage() {
    const { data: locationsData } = useLocations();
    const { data: lastLocationVisitsData } = useLastLocationVisits();
    const [lastLocationVisits, setLastLocationVisits] = useState([]);

	const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    
    const locations = Array.isArray(locationsData) ? locationsData : (locationsData ? Object.values(locationsData) : []);
    const addresses = locations.map((loc: any) => loc.address).filter(Boolean);
    // Function to get formatted address
    const getAddressString = (addressId?: number) => {
        const address = addresses.find(addr => addr.id === addressId);
        if (!address) return '';

        const { addressLine1, addressLine2, city, subdivision, postalCode, country } = address;
        return [addressLine1, addressLine2, city, subdivision, postalCode, country]
            .filter(Boolean)  // Remove empty values
            .join(', ');
    };

    // Filtering locations based on search term and active status
    // By default, only show active locations
    const filteredLocations = locations.filter(location => {
        // Hide inactive locations by default
        // Treat undefined/null as active (true) for backward compatibility
        const isActive = location.active !== undefined && location.active !== null 
          ? location.active 
          : true; // Default to true if undefined/null
        if (isActive === false) {
            return false;
        }
        
        const addressString = getAddressString(location.addressId);
        const searchLower = searchTerm.toLowerCase();
        return (
            location.name?.toLowerCase().includes(searchLower) ||
            addressString.toLowerCase().includes(searchLower)
        );
    });

    const breadcrumbs = [
        { label: 'Home', href: '/app/dashboard' },
        { label: 'Service' }, // No href for the current page
    ];

    useEffect(() => {
        if (lastLocationVisitsData) {
            const visits = Array.isArray(lastLocationVisitsData) 
                ? lastLocationVisitsData 
                : (lastLocationVisitsData.locationVisits || []);
            setLastLocationVisits(visits);
        }
    }, [lastLocationVisitsData]);
    


    const renderLastSiteVisit = (location: ILocation) => {

        if (!lastLocationVisits.length) return null;
        
        const lastVisit = lastLocationVisits.find((visit: any) => visit.locationId === location.id);
        if (lastVisit){
            return (
                <SiteVisitOverviewCard siteVisit={lastVisit} />
            )
        }
        return null;
    }

    return (
        <>
            <TitleBar
                breadcrumbs={breadcrumbs}
                title={t('locationService')}
                rightContent={<>
        
                </>}
            />
            <Box sx={{ padding: 2 }}>
                <TextField
                    label="Search Locations"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <Grid container spacing={2}>
                    {filteredLocations.map((location) => (
                        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 3 }} key={location.id}>
                            <Card sx={{
                                cursor: 'pointer',
                                height: 200, // Fixed card height
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: 4, // Adds a shadow on hover
                                    transform: 'scale(1.03)', // Slightly enlarges the card
                                }
                            }}
                                onClick={() => {
                                    navigate('/app/location-service-start/' + location.id)
                                }}
                            >
                                <CardContent sx={{
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ mb: 1 }}
                                    >
                                        {location.name}
                                    </Typography>
                                    {location.addressId && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%'
                                            }}
                                        >
                                            {getAddressString(location.addressId)}
                                        </Typography>
                                    )}
                                    {renderLastSiteVisit(location)}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>

    );
}

export default LocationServiceSearchPage;
